import React, {useState, useRef, useEffect} from 'react';
import axios from "axios";
import {toast} from "react-toastify";
import _ from "lodash";
import Input from "../../forms/Input";
import AssignProducts from "./products/AssignProducts";
import Button from "../../Button";
import {doubleBookedHelper} from "../../../helpers/DoubleBookedHelper";

const INSTALLATION_DEFAULTS = {
    id:                         '',
    start_at:                   '',
    end_at:                     '',
    notes:                      '',
    user_ids:                   [],
    product_ids:                [],
    has_overridden_products:    false
}

const CreateEditInstallation = (props) => {
    const {
        jobId           = null,
        orderId         = null,
        installationId  = null,
        onCancel        = () => {},
        onComplete      = () => {},
    } = props;

    const prepareDefaultValues = () => {
        const defaultInstallation = INSTALLATION_DEFAULTS;

        if (!!installationId && installationId !== "") {
            defaultInstallation.id = parseInt(installationId);
        }

        return defaultInstallation;
    }

    const [submitting, setSubmitting]               = useState(false);
    const [users, setUsers]                         = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [job, setJob]                             = useState(null);
    const [installation, setInstallation]           = useState(prepareDefaultValues());
    const [errors, setErrors]                       = useState(null);

    useEffect(() => {
        load();
    }, [jobId, orderId, installationId]);

    const load = () => {
        axios.get(route('web-api.job.order.installation.config', {job: jobId, order: orderId, installation_id: installationId}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    if (!!response.data.job) {
                        setJob(response.data.job);
                    }

                    if (!!response.data.products) {
                        setAvailableProducts(response.data.products);
                    }

                    if (!!response.data.users) {
                        setUsers(response.data.users);
                    }

                    if (!!response.data.installation) {
                        processInstallation(response.data.users, response.data.installation);
                    } else {
                        setInstallation(INSTALLATION_DEFAULTS);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                toast.error(<div>{"An error occurred"}</div>);
                setSubmitting(false);
            });
    }

    const processInstallation = (users, installation) => {
        const processedUsers = [];

        for (let installationUserIndex = 0; installationUserIndex < installation.user_ids.length; installationUserIndex++) {
            const userId = installation.user_ids[installationUserIndex];

            for (let userIndex = 0; userIndex < users.length; userIndex++) {
                const user = users[userIndex];

                if (user.value === userId) {
                    processedUsers.push(user);
                    break;
                }
            }
        }

        installation.user_ids = processedUsers;

        setInstallation(installation);
    }

    useEffect(() => {
        if (!!submitting) {
            submit();
        }
    }, [submitting]);

    const reset = () => {
        setInstallation(prepareDefaultValues());

        setSubmitting(false);
        setErrors(null);
    }

    const payload = () => {
        return {
            method: !!installationId ? 'patch' : 'post',
            url:    !!installationId ? route('web-api.job.order.installation.update', {job: jobId, order: orderId, installation: installationId}).toString() : route('web-api.job.order.installation.store', {job: jobId, order: orderId}).toString(),
            data:   {
                start_at:       installation.start_at,
                end_at:         installation.end_at,
                notes:          installation.notes,
                user_ids:       !!installation.user_ids && installation.user_ids.length > 0 ? installation.user_ids.map((user) => {
                    return user.value;
                }) : null,
                product_ids:    installation.product_ids,
            },
        }
    }

    const submit = () => {
        const data = payload();
        axios(data)
            .then((response) => {
                reset();

                if (!!response && !!response.data && !!response.data.double_booked) {
                    toast.warning(doubleBookedHelper(response));
                }

                onComplete(response.data.installation_id);
            })
            .catch((error) => {
                console.log(error);
                if (!!error.response.data && !!error.response.data.errors) {
                    setErrors(error.response.data.errors);
                    toast.error(<div>{!!error.response.data.message ? error.response.data.message : "An error occurred"}</div>);
                }

                setSubmitting(false);
            });
    }

    const handleClose = () => {
        if (!submitting) {
            reset();
            onCancel();
        }
    }

    const onChange = (field, value) => {
        const clone = _.cloneDeep(installation);

        clone[field] = value;

        setInstallation(clone);
    }

    return (
        <React.Fragment>
            <div className="row m-0 p-0">
                <div className="col-12 col-lg-6 m-0 p-0 pr-lg-2 bespoke-input">
                    <Input
                        name={"start_at"}
                        label={"Start Date"}
                        required={true}
                        type={"date"}
                        value={installation.start_at}
                        disabled={submitting}
                        onChange={(value) => onChange("start_at", value)}
                        error={!!errors && !!errors.start_at ? errors.start_at : null}
                    />
                </div>
                <div className="col-12 col-lg-6 m-0 p-0 pl-lg-2 bespoke-input">
                    <Input
                        name={"end_at"}
                        label={"End Date"}
                        required={true}
                        type={"date"}
                        value={installation.end_at}
                        disabled={submitting}
                        onChange={(value) => onChange("end_at", value)}
                        error={!!errors && !!errors.end_at ? errors.end_at : null}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 bespoke-input">
                    <Input
                        name={"user_ids"}
                        label={"Installer"}
                        required={true}
                        type={"searchable-select"}
                        options={users}
                        value={installation.user_ids}
                        disabled={submitting}
                        isMulti={true}
                        onChange={(value) => onChange("user_ids", value)}
                        error={!!errors && !!errors.user_ids ? errors.user_ids : null}
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0">
                <div className="col-12 m-0 p-0 bespoke-input">
                    <Input
                        name={"notes"}
                        label={"Notes"}
                        required={false}
                        type={"textarea"}
                        value={installation.notes}
                        disabled={submitting}
                        onChange={(value) => onChange("notes", value)}
                        error={!!errors && !!errors.notes ? errors.notes : null}
                    />
                </div>
            </div>
            <div className="row m-0 mb-4 p-0 pb-2 border-bottom">
                <div className="col-12 m-0 p-0">
                    <AssignProducts
                        installationId={installationId}
                        job={!!job ? job : null}
                        hasOverriddenProducts={!!installation ? installation.has_overridden_products : false}
                        availableProducts={availableProducts}
                        productIds={installation.product_ids}
                        onChange={(value) => onChange("product_ids", value)}
                        disabled={submitting}
                        errors={errors}
                    />
                </div>
            </div>
            <div className="row m-0 p-0">
                <div className="col-12 m-0 p-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex justify-content-start align-items-center">
                        <Button
                            onClick={() => setSubmitting(true)}
                            label={!!installationId ? "Save Changes" : "Create"}
                            classes={"btn btn-eh-lilac m-0 mr-4"}
                        />
                        <Button
                            onClick={() => handleClose()}
                            label={"Cancel"}
                            classes={"btn btn-outline-eh-off-black m-0 mr-4"}
                        />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default CreateEditInstallation;
